import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RotatingLines } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';
import PageHeader from '@components/common/PageHeader';
import Icon from '@components/common/Icon';
import Button from '@components/Button/Button';
import coursesService from '@/services/courses/coursesService';

const CourseDetailsPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation('course-details');

    const { data: response, isLoading, isError } = useQuery({
        queryKey: ['course', id],
        queryFn: () => coursesService.getCourse(id!),
        enabled: !!id,
    });

    const course = response?.data;

    // Features list for checkmarks
    const features = [
        t('features.f1'),
        t('features.f2'),
        t('features.f3'),
        t('features.f4'),
        t('features.f5'),
    ];

    // Cards data
    const overviewCards = [
        {
            title: t('highlights.h1_title'),
            description: t('highlights.h1_desc'),
            icon: 'users',
            bg: 'bg-[#F0F6FF]',
            iconColor: 'text-[#007AFF]',
        },
        {
            title: t('highlights.h2_title'),
            description: t('highlights.h2_desc'),
            icon: 'teamassignemeticonicon', // fallback to standard or specific icons
            bg: 'bg-[#FFF8E6]',
            iconColor: 'text-[#FF9500]',
        },
        {
            title: t('highlights.h3_title'),
            description: t('highlights.h3_desc'),
            icon: 'auditquestiondesign',
            bg: 'bg-[#E6F9F0]',
            iconColor: 'text-[#34C759]',
        },
        {
            title: t('highlights.h4_title'),
            description: t('highlights.h4_desc'),
            icon: 'shieldCheck',
            bg: 'bg-[#F6F0FF]',
            iconColor: 'text-[#AF52DE]',
        },
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-96 gap-3">
                <RotatingLines
                    width="44"
                    color="var(--primary-color)"
                    strokeWidth="4"
                    animationDuration="0.75"
                />
            </div>
        );
    }

    if (isError || !course) {
        return (
            <div className="p-5">
                <div className="bg-white border border-[var(--neutral-color)] rounded-2xl p-5 text-sm text-[var(--semantic-error-color)]">
                    {t('errors.load_failed')}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col pb-16">
            <PageHeader
                title={t('page_title')}
                breadcrumbItems={[
                    { label: t('breadcrumb_dashboard'), to: '/admin/dashboard' },
                    { label: t('breadcrumb_program'), to: '/admin/courses' },
                    { label: `${course.course_name}${t('breadcrumb_overview_suffix')}` },
                ]}
                actions={
                    <Button variant="ghost" onClick={() => navigate('/admin/courses')} className="border border-[var(--neutral-color)]">
                        <Icon name="arrowLeft" className="mr-2 w-3 2xl:w-4 h-auto" />
                        {t('buttons.back')}
                    </Button>
                }
            />

            <div className="px-5 flex flex-col gap-6 2xl:gap-8">
                {/* ── Overview Main Card ─────────────────────────────────────── */}
                <div className="bg-white border border-[var(--neutral-color)] rounded-3xl p-8 2xl:p-12 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] relative overflow-hidden">
                    <div className="absolute right-0 top-0 opacity-10 pointer-events-none select-none">
                        {/* Decorative background grid pattern */}
                        <div className="w-[300px] h-[300px] bg-[radial-gradient(var(--primary-color)_1.5px,transparent_1.5px)] [background-size:16px_16px]" />
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center relative z-10">
                        {/* Left Side: Program Branding */}
                        <div className="lg:col-span-6 flex flex-col">
                            <h1 className="text-[32px] 2xl:text-[44px] font-bold text-[var(--secondary-color)] leading-tight mb-4 tracking-tight">
                                {course.course_name}
                            </h1>
                            <div className="w-16 h-1 bg-[var(--primary-color)] rounded-full mb-6" />
                            <p className="text-[16px] 2xl:text-[18px] text-[var(--subtitle-color)] leading-relaxed font-normal max-w-lg">
                                {course.description}
                            </p>
                        </div>

                        {/* Right Side: Checkmarks List */}
                        <div className="lg:col-span-6 bg-[var(--primary-canvas-color)] border border-[var(--neutral-color)] rounded-2xl p-6 2xl:p-8 flex flex-col gap-4">
                            {features.map((feature, idx) => (
                                <div key={idx} className="flex items-center gap-3.5">
                                    <div className="w-6 h-6 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                                        <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                        </svg>
                                    </div>
                                    <span className="text-[14px] 2xl:text-[16px] font-semibold text-[var(--secondary-color)]">
                                        {feature}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Key Highlights Cards Grid ─────────────────────────────── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                    {overviewCards.map((card, idx) => {
                        // Resolve icons properly
                        const iconName = card.icon === 'teamassignemeticonicon' ? 'users' : card.icon;
                        return (
                            <div
                                key={idx}
                                className="bg-white border border-[var(--neutral-color)] rounded-2xl p-6 flex flex-col gap-5 hover:shadow-[0_12px_24px_-8px_rgba(0,0,0,0.08)] transition-all duration-300 group hover:-translate-y-1"
                            >
                                <div className={`w-12 h-12 rounded-2xl ${card.bg} flex items-center justify-center shrink-0`}>
                                    <Icon name={iconName as any} size={24} className={`${card.iconColor}`} />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <h3 className="text-[16px] 2xl:text-[18px] font-bold text-[var(--secondary-color)] group-hover:text-[var(--primary-color)] transition-colors">
                                        {card.title}
                                    </h3>
                                    <p className="text-[13px] 2xl:text-[14px] text-[var(--subtitle-color)] leading-relaxed font-normal">
                                        {card.description}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ── Bottom Quality Banner ──────────────────────────────────── */}
                <div className="bg-[#F0F5FF] border border-[#D0E2FF] rounded-2xl px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#007AFF] flex items-center justify-center shrink-0 text-white">
                            <Icon name="checkCircle" size={16} />
                        </div>
                        <span className="text-[14px] font-bold text-[#0040B0]">
                            {t('quality_banner.title')}
                        </span>
                    </div>
                    <div className="h-px w-full md:w-px md:h-6 bg-[#C4D5F6]" />
                    <span className="text-[13px] font-medium text-[#4B68A0]">
                        {t('quality_banner.subtitle')}
                    </span>
                    <div className="h-px w-full md:w-px md:h-6 bg-[#C4D5F6]" />
                    <span className="text-[13px] font-semibold text-[#007AFF]">
                        {t('quality_banner.footer')}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default CourseDetailsPage;
